import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowRight, Loader2, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { supplierKeys } from "@/lib/queryKeys";
import {
  listSuppliers,
  updateSupplier,
  type Supplier,
} from "@/services/supplierService";

/**
 * Per-supplier auto-accept summary. Replaces the legacy emisor-RUC
 * allowlist card (the table was dropped; the flag now lives on
 * supplier.autoAckCfeOnReceive). Disabling here flips the supplier
 * flag without soft-deleting the row — the proveedor stays in the
 * /suppliers list with auto-accept turned off.
 *
 * Hidden when no suppliers have auto-accept enabled; the user discovers
 * the flow via the post-accept prompt and the supplier detail page.
 */
export function SettingsAutoAcceptRucsCard(): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: supplierKeys.list({
      page: 1,
      limit: 50,
      autoAckCfeOnReceive: true,
    }),
    queryFn: () =>
      listSuppliers({ page: 1, limit: 50, autoAckCfeOnReceive: true }),
    staleTime: 30 * 1000,
  });

  const disableMutation = useMutation({
    mutationFn: (id: string) =>
      updateSupplier(id, { autoAckCfeOnReceive: false }),
    onSuccess: () => {
      toast.success(
        t(
          "settings.autoAcceptRucs.removeToast",
          "Aceptación automática desactivada para este proveedor.",
        ),
      );
      void qc.invalidateQueries({ queryKey: supplierKeys.all });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Error");
    },
  });

  const suppliers: Supplier[] = query.data?.data ?? [];

  if (!query.isLoading && suppliers.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base text-foreground">
          {t(
            "settings.autoAcceptRucs.title",
            "Aceptación automática por proveedor",
          )}
        </CardTitle>
        <CardDescription>
          {t(
            "settings.autoAcceptRucs.description",
            "Comprobantes recibidos de estos proveedores se aceptan automáticamente al ingresar — sin pedirte confirmación. Podés desactivarlos aquí, o desde la ficha del proveedor.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ul className="divide-y divide-[var(--fx-ink-100)]">
            {suppliers.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-3 py-2.5"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/suppliers/${s.id}`)}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="truncate text-sm font-medium">
                    {s.displayName?.trim() || s.legalName}
                  </div>
                  {s.rut?.trim() ? (
                    <div className="text-xs text-muted-foreground tabular-nums">
                      RUT {s.rut}
                    </div>
                  ) : null}
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => disableMutation.mutate(s.id)}
                  disabled={disableMutation.isPending}
                  className="shrink-0"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t("settings.autoAcceptRucs.remove", "Desactivar")}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="border-t border-border pt-3">
          <Button asChild variant="ghost" size="sm" className="gap-2">
            <Link to="/suppliers">
              {t(
                "settings.autoAcceptRucs.manageAll",
                "Administrar todos los proveedores",
              )}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
